import React from "react";
import PropTypes from "prop-types";
import AppHeader from "../AppHeader";
import ViewSwitcher from "../../ViewSwitcher";
import { DeviceList, DeviceManagementContainer } from "../DeviceManagement";
import "./DeviceManagementView.css";

/**
 * DeviceManagementView - Wrapper component for device management view
 * 
 * Renders the complete device management interface including:
 * - AppHeader with device-specific title and navigation
 * - Context separator bar
 * - ViewSwitcher (detailed/simplified modes)
 * - DeviceList selector
 * - DeviceManagementContainer with device panels and credentials
 */
const DeviceManagementView = ({
  // Header props
  theme,
  activeLogo,
  onToggleLogo,
  organizationId,
  organizationName,
  installationId,
  installationName,
  orgInstallContext,
  onNavigateToPatrons,
  onOpenAdvancedSettings,
  onOpenInstallationActions,
  onOpenInstallationManagement,
  onOpenOrganizationManagement,
  onToggleDevStreams,
  deviceCount,
  allDeviceStatesLoaded,
  currentOrgSettings,
  
  // Device list props
  devices,
  selectedDeviceId,
  onDeviceSelect,
  onRefresh,
  isRefreshing,
  onAddDevice,
  
  // View mode
  deviceViewMode,
  onViewChange,
  
  // Device management container props
  deviceStates,
  actionHistory,
  actionHistoryLimit,
  onActionHistoryLimitChange,
  loadingAction,
  onExecuteAction,
  onDownloadSettings,
  onUpdateFirmware,
  isDownloadingSettings,
  isSyncing,
  onEditDisplayNameClick,
  onEditCredentialsSettingClick,
  connectivityStatusUpdateTime,
  collapseStates,
  onToggleCollapse,
  components,
  onDragEnd,
  credentialsSection,
}) => {
  return (
    <>
      <AppHeader
        currentView="devices"
        title="The Value of Real-Time Device Management with Mobile Credentials"
        theme={theme}
        activeLogo={activeLogo}
        onToggleLogo={onToggleLogo}
        organizationId={organizationId}
        organizationName={organizationName}
        installationId={installationId}
        installationName={installationName}
        orgInstallContext={orgInstallContext}
        onNavigateToPatrons={onNavigateToPatrons}
        onNavigateToDevices={() => {}} // No-op in device view
        onOpenAdvancedSettings={onOpenAdvancedSettings}
        onOpenInstallationActions={onOpenInstallationActions}
        onOpenInstallationManagement={onOpenInstallationManagement}
        onOpenOrganizationManagement={onOpenOrganizationManagement}
        onToggleDevStreams={onToggleDevStreams}
        deviceCount={deviceCount}
        devices={devices}
        onDevicesClaimed={onRefresh}
        allDeviceStatesLoaded={allDeviceStatesLoaded}
        currentOrgSettings={currentOrgSettings}
      />

      {/* Separator line */}
      <div className="context-bar"></div>

      {/* View Switcher - only show when device is selected */}
      {selectedDeviceId && (
        <ViewSwitcher
          currentView={deviceViewMode}
          onViewChange={onViewChange}
        />
      )}

      {/* Device Selector */}
      <DeviceList
        devices={devices}
        selectedDeviceId={selectedDeviceId}
        onDeviceSelect={onDeviceSelect}
        onRefresh={onRefresh}
        isRefreshing={isRefreshing}
        onAddDevice={onAddDevice}
        viewMode={deviceViewMode}
      />

      {/* Device Management Views */}
      {selectedDeviceId && (
        <>
          <DeviceManagementContainer
            deviceId={selectedDeviceId}
            deviceState={deviceStates[selectedDeviceId]}
            viewMode={deviceViewMode}
            actionHistory={actionHistory}
            actionHistoryLimit={actionHistoryLimit}
            onActionHistoryLimitChange={onActionHistoryLimitChange}
            loadingAction={loadingAction}
            onExecuteAction={onExecuteAction}
            onDownloadSettings={onDownloadSettings}
            onUpdateFirmware={onUpdateFirmware}
            isDownloadingSettings={isDownloadingSettings}
            isSyncing={isSyncing}
            onEditDisplayNameClick={onEditDisplayNameClick}
            onEditCredentialsSettingClick={onEditCredentialsSettingClick}
            connectivityStatusUpdateTime={connectivityStatusUpdateTime}
            collapseStates={collapseStates}
            onToggleCollapse={onToggleCollapse}
            components={components}
            onDragEnd={onDragEnd}
            credentialsSection={credentialsSection}
          />
        </>
      )}
    </>
  );
};

DeviceManagementView.propTypes = {
  // Header props
  theme: PropTypes.string.isRequired,
  activeLogo: PropTypes.string.isRequired,
  onToggleLogo: PropTypes.func.isRequired,
  organizationId: PropTypes.string,
  organizationName: PropTypes.string,
  installationId: PropTypes.string,
  installationName: PropTypes.string,
  orgInstallContext: PropTypes.object,
  onNavigateToPatrons: PropTypes.func.isRequired,
  onOpenAdvancedSettings: PropTypes.func.isRequired,
  onOpenInstallationActions: PropTypes.func.isRequired,
  onOpenInstallationManagement: PropTypes.func.isRequired,
  onOpenOrganizationManagement: PropTypes.func.isRequired,
  onToggleDevStreams: PropTypes.func.isRequired,
  deviceCount: PropTypes.number.isRequired,
  allDeviceStatesLoaded: PropTypes.bool.isRequired,
  currentOrgSettings: PropTypes.object,
  
  // Device list props
  devices: PropTypes.array.isRequired,
  selectedDeviceId: PropTypes.string,
  onDeviceSelect: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  isRefreshing: PropTypes.bool.isRequired,
  onAddDevice: PropTypes.func.isRequired,
  
  // View mode
  deviceViewMode: PropTypes.oneOf(["detailed", "simplified"]).isRequired,
  onViewChange: PropTypes.func.isRequired,
  
  // Device management container props
  deviceStates: PropTypes.object.isRequired,
  actionHistory: PropTypes.array.isRequired,
  actionHistoryLimit: PropTypes.number.isRequired,
  onActionHistoryLimitChange: PropTypes.func.isRequired,
  loadingAction: PropTypes.bool.isRequired,
  onExecuteAction: PropTypes.func.isRequired,
  onDownloadSettings: PropTypes.func.isRequired,
  onUpdateFirmware: PropTypes.func,
  isDownloadingSettings: PropTypes.bool.isRequired,
  isSyncing: PropTypes.bool.isRequired,
  onEditDisplayNameClick: PropTypes.func.isRequired,
  onEditCredentialsSettingClick: PropTypes.func,
  connectivityStatusUpdateTime: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  collapseStates: PropTypes.shape({
    deviceState: PropTypes.bool.isRequired,
    deviceActions: PropTypes.bool.isRequired,
    lastAction: PropTypes.bool.isRequired,
    deviceSettings: PropTypes.bool,
  }).isRequired,
  onToggleCollapse: PropTypes.shape({
    deviceState: PropTypes.func.isRequired,
    deviceActions: PropTypes.func.isRequired,
    lastAction: PropTypes.func.isRequired,
    deviceSettings: PropTypes.func,
  }).isRequired,
  components: PropTypes.array.isRequired,
  onDragEnd: PropTypes.func.isRequired,
  credentialsSection: PropTypes.node,
};

export default DeviceManagementView;
